import type { Meta, StoryObj } from '@storybook/angular';
import { expect } from 'storybook/test';
import { HeartSolidIconComponent } from '../../../app/core/svg-icons/heart-solid-icon.component';

const meta: Meta<HeartSolidIconComponent> = {
  title: 'Atoms/Icons/HeartSolidIcon',
  component: HeartSolidIconComponent,
  parameters: {
    docs: {
      description: {
        component: 'A heart icon component with customizable size, stroke and fill colors. Perfect for favorites, likes, and wishlist functionality.',
      },
    },
  },
  argTypes: {
    width: {
      control: { type: 'text' },
      description: 'Width of the icon',
    },
    height: {
      control: { type: 'text' },
      description: 'Height of the icon',
    },
    stroke: {
      control: { type: 'color' },
      description: 'Stroke color of the heart icon',
    },
    fill: {
      control: { type: 'color' },
      description: 'Fill color of the heart icon',
    },
  },
  args: {
    width: '24px',
    height: '24px',
    stroke: '#5C0A31',
    fill: '#5C0A31',
  },
};

export default meta;
type Story = StoryObj<HeartSolidIconComponent>;

export const Default: Story = {
  args: {
    width: '24px',
    height: '24px',
    stroke: '#5C0A31',
    fill: '#5C0A31',
  },
  render: (args) => ({
    props: args,
    template: `<app-heart-solid-icon 
      [width]="width" 
      [height]="height" 
      [stroke]="stroke" 
      [fill]="fill">
    </app-heart-solid-icon>`,
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-heart-solid-icon')).toBeTruthy();
  },
};

export const Red: Story = {
  args: {
    width: '32px',
    height: '32px',
    stroke: '#DC3545',
    fill: '#DC3545',
  },
  render: (args) => ({
    props: args,
    template: `<app-heart-solid-icon 
      [width]="width" 
      [height]="height" 
      [stroke]="stroke" 
      [fill]="fill">
    </app-heart-solid-icon>`,
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-heart-solid-icon')).toBeTruthy();
  },
};

export const Outlined: Story = {
  args: {
    width: '28px',
    height: '28px',
    stroke: '#5C0A31',
    fill: 'none',
  },
  render: (args) => ({
    props: args,
    template: `<app-heart-solid-icon 
      [width]="width" 
      [height]="height" 
      [stroke]="stroke" 
      [fill]="fill">
    </app-heart-solid-icon>`,
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-heart-solid-icon')).toBeTruthy();
  },
};

export const Large: Story = {
  args: {
    width: '48px',
    height: '48px',
    stroke: '#5C0A31',
    fill: '#5C0A31',
  },
  render: (args) => ({
    props: args,
    template: `<app-heart-solid-icon 
      [width]="width" 
      [height]="height" 
      [stroke]="stroke" 
      [fill]="fill">
    </app-heart-solid-icon>`,
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-heart-solid-icon')).toBeTruthy();
  },
};

export const ColorVariants: Story = {
  render: () => ({
    template: `
      <div style="display: flex; gap: 1rem; align-items: center; flex-wrap: wrap;">
        <div style="text-align: center;">
          <div>Wine Red (Default)</div>
          <app-heart-solid-icon width="32px" height="32px" stroke="#5C0A31" fill="#5C0A31"></app-heart-solid-icon>
        </div>
        <div style="text-align: center;">
          <div>Red</div>
          <app-heart-solid-icon width="32px" height="32px" stroke="#DC3545" fill="#DC3545"></app-heart-solid-icon>
        </div>
        <div style="text-align: center;">
          <div>Pink</div>
          <app-heart-solid-icon width="32px" height="32px" stroke="#E91E63" fill="#E91E63"></app-heart-solid-icon>
        </div>
        <div style="text-align: center;">
          <div>Gray</div>
          <app-heart-solid-icon width="32px" height="32px" stroke="#6C757D" fill="#6C757D"></app-heart-solid-icon>
        </div>
        <div style="text-align: center;">
          <div>Outlined</div>
          <app-heart-solid-icon width="32px" height="32px" stroke="#5C0A31" fill="none"></app-heart-solid-icon>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Different color variants of the heart icon.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-heart-solid-icon')).toBeTruthy();
  },
};

export const SizeVariants: Story = {
  render: () => ({
    template: `
      <div style="display: flex; gap: 1rem; align-items: center;">
        <div style="text-align: center;">
          <div>Small (16px)</div>
          <app-heart-solid-icon width="16px" height="16px"></app-heart-solid-icon>
        </div>
        <div style="text-align: center;">
          <div>Medium (24px)</div>
          <app-heart-solid-icon width="24px" height="24px"></app-heart-solid-icon>
        </div>
        <div style="text-align: center;">
          <div>Large (32px)</div>
          <app-heart-solid-icon width="32px" height="32px"></app-heart-solid-icon>
        </div>
        <div style="text-align: center;">
          <div>Extra Large (48px)</div>
          <app-heart-solid-icon width="48px" height="48px"></app-heart-solid-icon>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Different size variants of the heart icon.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-heart-solid-icon')).toBeTruthy();
  },
};
